import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectConduitSizes, selectConduitTypes } from '../../../store/selectors/databaseSelectors';
import { addConduitSize, addConduitType } from '../../../store/slices/databaseSlice';
import { conduitService } from '../../../services';
import { Conduit, ConduitCategory } from '../../../types';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import toast from 'react-hot-toast';

interface ConduitForm {
  category: ConduitCategory;
  value: string;
}

type ConduitTab = 'sizes' | 'types';

const CONDUIT_TABS: { id: ConduitTab; label: string; category: ConduitCategory }[] = [
  { id: 'sizes', label: 'Sizes', category: ConduitCategory.SIZE },
  { id: 'types', label: 'Types', category: ConduitCategory.TYPE }
];

export const ConduitManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  
  // Get data from Redux
  const conduitSizes = useAppSelector(selectConduitSizes);
  const conduitTypes = useAppSelector(selectConduitTypes);
  
  // Form states
  const [conduitForm, setConduitForm] = useState<ConduitForm>({
    category: ConduitCategory.SIZE,
    value: ''
  });
  
  // UI states
  const [activeTab, setActiveTab] = useState<ConduitTab>('sizes');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localConduitData, setLocalConduitData] = useState({
    sizes: conduitSizes,
    types: conduitTypes
  });

  // Initialize local state with Redux data
  useEffect(() => {
    setLocalConduitData({
      sizes: conduitSizes,
      types: conduitTypes
    });
  }, [conduitSizes, conduitTypes]);

  // Update form category when tab changes
  useEffect(() => {
    const currentTab = CONDUIT_TABS.find(tab => tab.id === activeTab);
    if (currentTab) {
      setConduitForm(prev => ({ ...prev, category: currentTab.category }));
    }
  }, [activeTab]);

  // Get current data for active tab
  const getCurrentData = (): Conduit[] => {
    switch (activeTab) {
      case 'sizes': return localConduitData.sizes;
      case 'types': return localConduitData.types;
      default: return [];
    }
  };

  // Get dispatch function for active tab
  const getDispatchFunction = () => {
    switch (activeTab) {
      case 'sizes': return addConduitSize;
      case 'types': return addConduitType;
      default: return addConduitSize;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!conduitForm.value.trim()) {
      toast('Please enter a value', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const newConduit = await conduitService.createConduit({
        category: conduitForm.category,
        value: conduitForm.value.trim()
      });
      
      // Update Redux state
      const dispatchFunction = getDispatchFunction();
      dispatch(dispatchFunction(newConduit));
      
      // Update local state
      setLocalConduitData(prev => {
        const updated = { ...prev };
        switch (activeTab) {
          case 'sizes': updated.sizes = [...prev.sizes, newConduit]; break;
          case 'types': updated.types = [...prev.types, newConduit]; break;
        }
        return updated;
      });
      
      // Reset form
      setConduitForm(prev => ({ ...prev, value: '' }));
      
      toast.success(`Conduit ${CONDUIT_TABS.find(t => t.id === activeTab)?.label?.slice(0, -1)} created successfully!`, TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating conduit item:', error);
      toast.error('Failed to create conduit item. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentData = getCurrentData();

  const getPlaceholder = () => {
    switch (activeTab) {
      case 'sizes': return 'e.g., 1/2", 3/4", 1"';
      case 'types': return 'e.g., EMT, PVC, LFMC';
      default: return 'Enter value';
    }
  };

  return (
    <div className="p-4">
      {/* Tabs */}
      <div className="flex border-b border-slate-200 mb-4">
        {CONDUIT_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 font-medium text-sm border-b-2 transition-colors duration-200 mr-4 ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label} ({currentData.length})
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">
            Add New Conduit {CONDUIT_TABS.find(t => t.id === activeTab)?.label?.slice(0, -1)}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                {activeTab === 'sizes' ? 'Size *' : 'Type *'}
              </label>
              <input
                type="text"
                value={conduitForm.value}
                onChange={(e) => setConduitForm(prev => ({ ...prev, value: e.target.value }))}
                placeholder={getPlaceholder()}
                className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <p className="text-xs text-slate-500 mt-1">
                {activeTab === 'sizes' 
                  ? 'Enter conduit size (e.g., 1/2", 3/4", 1")'
                  : 'Enter conduit type (e.g., EMT, PVC, LFMC)'}
              </p>
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting || !conduitForm.value.trim()}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? 'Creating...' : `Create ${CONDUIT_TABS.find(t => t.id === activeTab)?.label?.slice(0, -1)}`}
            </button>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">
              Existing Conduit {CONDUIT_TABS.find(t => t.id === activeTab)?.label}
            </h3>
            <span className="text-xs text-slate-500">
              {currentData.length} items
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {currentData.length > 0 ? (
              <div className="flex flex-wrap gap-2 p-2">
                {currentData.map((item) => (
                  <div key={item.id} className="inline-flex items-center px-3 py-1 bg-green-50 text-green-800 rounded-full border border-green-200 hover:bg-green-100 transition-colors">
                    <span className="text-xs font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-xs text-center py-8">
                No conduit {CONDUIT_TABS.find(t => t.id === activeTab)?.label?.toLowerCase()} found
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};